import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';
import { PublicShell } from '@/components/visual/PublicShell';
import { LocaleLink } from '@/components/LocaleLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, ArrowLeft, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { localePath } = useLocale();
  const token = searchParams.get('token');

  const [mode] = useState(token ? 'reset' : 'forgot');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email, lang: i18n.language }, { withCredentials: true });
      setSent(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reset.somethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error(t('reset.passwordsMismatch'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      toast.success(t('reset.resetSuccess'));
      setTimeout(() => navigate(localePath('/calculator')), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reset.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      <main id="main-content" tabIndex={-1} className="auth-main">
        <div>
          <Card className="bg-white border-[#DDE1E5] rounded-lg shadow-card" data-testid="reset-password-card">
            <CardHeader className="space-y-2">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#527F19]/10 mb-1">
                <KeyRound className="w-5 h-5 text-[#527F19]" />
              </div>
              <h1 data-testid="reset-password-title">
                {mode === 'forgot' ? t('reset.resetTitle') : t('reset.newPasswordTitle')}
              </h1>
              <p className="text-sm text-[#66707A]">
                {mode === 'forgot'
                  ? t('reset.resetSubtitle')
                  : t('reset.newPasswordSubtitle')}
              </p>
            </CardHeader>
            <CardContent>
              {mode === 'forgot' && !sent && (
                <form onSubmit={handleForgot} className="space-y-4">
                  <div>
                    <Label htmlFor="reset-email" className="text-[#66707A]">{t('reset.emailLabel')}</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-[#8A929B]" />
                      <Input
                        id="reset-email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                        placeholder={t('reset.emailPlaceholder')}
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#527F19] hover:bg-[#3F6114] text-white shadow-card transition-colors"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? t('reset.sending') : t('reset.sendReset')}
                  </Button>
                  <div className="text-center">
                    <LocaleLink to="/auth" className="text-sm text-[#527F19] hover:underline" data-testid="back-to-login-link">
                      <ArrowLeft className="inline w-3 h-3 mr-1" /> {t('reset.backToSignIn')}
                    </LocaleLink>
                  </div>
                </form>
              )}

              {mode === 'forgot' && sent && (
                <div className="text-center py-4 space-y-3" data-testid="reset-link-sent">
                  <CheckCircle2 className="w-12 h-12 text-[#78B928] mx-auto" />
                  <p className="font-display font-semibold text-[#171A1D]">{t('reset.linkSent')}</p>
                  <p className="text-sm text-[#66707A]">
                    {t('reset.checkEmail')}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4 border-[#DDE1E5] text-[#3A4249] hover:text-[#527F19] hover:border-[#527F19]/40"
                    onClick={() => { setSent(false); setEmail(''); }}
                    data-testid="try-again-btn"
                  >
                    {t('reset.tryAnother')}
                  </Button>
                </div>
              )}

              {mode === 'reset' && (
                <form onSubmit={handleReset} className="space-y-4">
                  <div>
                    <Label htmlFor="new-password" className="text-[#66707A]">{t('reset.newPasswordLabel')}</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-[#8A929B]" />
                      <Input
                        id="new-password"
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                        placeholder={t('reset.passPlaceholder')}
                        minLength={8}
                        required
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-[#66707A]">{t('reset.confirmLabel')}</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-[#8A929B]" />
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                        className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                        placeholder={t('reset.confirmPlaceholder')}
                        minLength={8}
                        required
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#527F19] hover:bg-[#3F6114] text-white shadow-card transition-colors"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? t('reset.resetting') : t('reset.resetPassword')}
                  </Button>
                  <div className="text-center">
                    <LocaleLink to="/auth" className="text-sm text-[#527F19] hover:underline" data-testid="back-to-login-link-reset">
                      <ArrowLeft className="inline w-3 h-3 mr-1" /> {t('reset.backToSignIn')}
                    </LocaleLink>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </PublicShell>
  );
}
